import { useRef, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider,
    Icon
} from "@mui/material";

import { Button } from "@gpstrackit/react-controls";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import { Tooltip } from "@gpstrackit/react-controls";

import {
    createPMUserOpenModal,
    pmUsersGetUserRequest,
    deletePMUserOpenModal,
    resetPasswordOpenModal,
    pmUserForceLogoutOpenModal,
    changeListOpenModal,
    resetEmailOpenModal,
    resendInviteOpenModal,
    activitiesSearchOpenModal
} from 'Redux/actions';

//Helpers
import { getDateFormatted, idsValidation } from 'Core/data/Helpers';

//Views
import CreateEditUser from 'Modules/pmUsers/.components/createEditUser';
import DeleteUser from 'Modules/pmUsers/.components/deleteUser';
import ForceLogout from 'Modules/pmUsers/.components/forceLogout';
import GeneralDetails from 'Modules/activities/.components/generalDetails';
import ChangeList from 'Modules/activities/.components/changeList';
import Activities from "Modules/activities/.components/tab";
import ResetEmail from 'Modules/.common/.components/resetEmail';
import ResendInvite from 'Modules/.common/.components/resendInvite';
import ResetPassword from 'Modules/.common/.components/resetPassword';

//Notification
import { NotificationManager } from "Components/Notifications";

//Permission
import { HasPermission } from "Core/security"

const PMUsers = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();

    //Local states
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([
        {
            key: "id",
            field: "id",
            title: messages['pmusers_main_id'],
            width: 80,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            position: 1,
            functionString: 'return row?.id',
            code: "pmusers_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "firstName",
            field: "firstName",
            title: messages['pmusers_main_name'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return `${row?.firstName} ${row?.lastName}`;',
            code: "pmusers_main_name",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', width: 180, justifyContent: 'space-between' }}>
                        <span>{item?.firstName}</span>
                        <Tooltip title={messages['common_show_changes']}>
                            <Icon onClick={() => {
                                dispatch(changeListOpenModal(true, { entityName: "User", entityId: item.id, compId: -1 }))
                            }} style={{ cursor: 'pointer' }}>info</Icon>
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: "lastName",
            field: "lastName",
            title: messages['pmusers_main_last_name'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.lastName',
            code: "pmusers_main_last_name",
            render: (item) => {
                return <span>{item?.lastName}</span>
            }
        },
        {
            key: "username",
            field: "username",
            title: messages['pmusers_main_username'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.username',
            code: "pmusers_main_username",
            render: (item) => {
                return <span>{item?.username}</span>
            }
        },
        {
            key: "email",
            field: "email",
            title: messages['pmusers_main_email'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.email',
            code: "pmusers_main_email",
            render: (item) => {
                return <span>{item?.email}</span>
            }
        },
        {
            key: "status",
            field: "status",
            title: messages['pmusers_main_status'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.status',
            code: "pmusers_main_status",
            render: (item) => {
                return <ChipStatus label={item?.status} />
            }
        },
        {
            key: "registrationStatus",
            field: "registrationStatus",
            title: messages['pmusers_main_registration_status'],
            width: 200,
            show: true,
            sort: false,
            locked: false,
            resize: true,
            code: "pmusers_main_registration_status",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        <ChipStatus label={item?.registrationStatus} messages={messages} />
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.registrationStatus'
        },
        {
            key: "roles",
            field: "roles { id name }",
            title: messages['pmusers_main_roles'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.roles[0]?.name',
            code: "pmusers_main_roles",
            render: (item) => {
                let roles = item.roles.map((item) => `${item.name}`).join(' , ')
                return (
                    <div>
                        {roles}
                    </div>
                )
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['pmusers_main_created'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "pmusers_main_created",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['pmusers_main_updated'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "pmusers_main_updated",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },

    ])
    const [searchFields, setSearchFields] = useState(["firstName", "lastName", "username", "email", "status"]);

    const user = useSelector(state => state.securityRedux.user);
    const generalDetailsOpenModal = useSelector(state => state.generalDetailsRedux.openModal);
    const changeListOpenModalA = useSelector(state => state.changeListRedux.openModal);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    const openModalResetEmail = useSelector(state => state.resetEmailRedux.openModal);
    const openModalResendInvite = useSelector(state => state.resendInviteRedux.openModal);
    const resetPasswordOModal = useSelector(state => state.resetPasswordRedux.openModal);

    let innerHeight = window.innerHeight

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW CONFIG OBJECTS
    // ═══════════════════════════════════════════════════════════════════════════

    const graphqlConfig = useMemo(() => ({
        function: "searchUsers",
        sortType: "UserSortInput",
        conditionsType: "UserSearchInput",
        customQuery: "id lastName",
        dynamicQuery: true,
    }), []);

    const searchConfig = useMemo(() => ({
        conditions: searchFields,
        fields: searchFields,
        optionValue: "id",
        optionLabel: "username",
        extraField: "firstName lastName username",
        openCustomOptions: (option) => {
            if(!option.firstName){
                return option?.label || option.id
            }
            return `${option?.firstName || ""} ${option?.lastName || ""} - ${option?.username || ""}`
        },
    }), []);

    const exportConfig = useMemo(() => ({
        fileName: "pmusers",
    }), []);

    const uiConfig = useMemo(() => ({
        hideSplit: true,
        hideFilters: true,
        topHeight: 210,
    }), []);

    const selectionConfig = useMemo(() => ({
        disabled: false,
    }), []);

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        let currentConditions = {}
        let currentTags = {}
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'firstName');

        let tagsById = currentTags.filter((item) => !isNaN(parseInt(item.id)));
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter(item => isNaN(parseInt(item.id)))
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.username))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions, newColumns });
        else tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
    };

    /**
     * Handle column changes
     */
    const handleChangeColumns = (items) => setColumns(items);

    /**
     * Handle total count changes
     */
    const handleChangeTotal = (total) => setTotal(total);

    /**
     * Menu options for context menu
     */
    const menuOptions = () => {
        return [
            messages['edit'],
            messages['delete'],
            messages['users_tab_resend_invite'],
            messages['pmusers_main_reset_password'],
            messages['users_tab_reset_user_email'],
            messages['pmusers_main_force_logout']
        ]
    };

    /**
     * Handle context menu clicks
     */
    const handleClickMenu = (event, item) => {
        if (event === messages['edit']) {
            if (HasPermission(user, "usr", "u")) {
                dispatch(createPMUserOpenModal(item.id));
                dispatch(pmUsersGetUserRequest(item.id));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['edit'],
                    3000,
                );
            }
        }
        if (event === messages['delete']) {
            if (HasPermission(user, "usr", "d")) {
                dispatch(deletePMUserOpenModal([item]))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['delete'],
                    3000,
                );
            }
        }
        if (event === messages['pmusers_main_reset_password']) {
            if (HasPermission(user, "usrrp", "u")) {
                if (item?.registrationStatus === "Active") {
                    let entity = { ...item, entityName: "User" }
                    dispatch(resetPasswordOpenModal(entity))
                } else {
                    NotificationManager.warning(
                        messages['users_tab_warning_message_reset_password'],
                        messages['pmusers_main_reset_password'],
                        3000,
                    );
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['pmusers_main_reset_password'],
                    3000,
                );
            }
        }
        if (event === messages['pmusers_main_force_logout']) {
            dispatch(pmUserForceLogoutOpenModal(true, item));
        }
        if (event === messages['users_tab_reset_user_email']) {
            if (HasPermission(user, "rsteml", "u")) {
                let entity = { ...item, entityName: "User" }
                dispatch(resetEmailOpenModal({ entity, callback: () => { tableRef?.current?.getData({}) } }))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
        if (event === messages['users_tab_resend_invite']) {
            if (HasPermission(user, "rsndinvt", "u")) {
                if (item?.registrationStatus === "Active") {
                    NotificationManager.warning(
                        messages['users_tab_warning_message_resend'],
                        messages['users_tab_users'],
                        3000,
                    );
                } else {
                    let entity = { ...item, entityName: "User" }
                    dispatch(resendInviteOpenModal({entity, callback: () => { tableRef?.current?.getData({}) }}))
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['users_tab_users'],
                    5000,
                );
            }
        }
    };

    /**
     * Toolbar options
     */
    const toolbarOptions = [
        {
            id: "edit-user",
            single: true,
            label: messages['pmusers_main_edit_user'],
            show: true,
            disabled: !HasPermission(user, "usr", "u"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                if (currentItem.id) {
                    dispatch(createPMUserOpenModal(currentItem.id));
                    dispatch(pmUsersGetUserRequest(currentItem.id));
                }
            }
        },
        {
            id: "delete-user",
            multiple: true,
            label: messages['pmusers_main_delete_user'],
            show: true,
            disabled: !HasPermission(user, "usr", "d"),
            action: (items) => {
                dispatch(deletePMUserOpenModal(items));
            }
        },
        {
            id: "change-password",
            single: true,
            label: messages['pmusers_main_reset_password'],
            show: true,
            disabled: !HasPermission(user, "usrrp", "u"),
            action: (items) => {
                dispatch(resetPasswordOpenModal(items[0].id));
            }
        },
        {
            id: "force-logout",
            single: true,
            label: messages['pmusers_main_force_logout'],
            show: true,
            disabled: false /* !HasPermission(user, "usrrp", "u") */,
            action: (items) => {
                dispatch(pmUserForceLogoutOpenModal(true, items[0]));
            }
        }
    ];

    /**
     * Custom column with activities action
     */
    const customColumn = {
        render: (row) => {
            return (
                <div>
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "User", entityId: row.id }, "pm-users-tab-modal"))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                </div>
            )
        }
    };

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['pmusers_main_title']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons">
                        <Button
                            disabled={!HasPermission(user, "usr", "c")}
                            className="btn-dark-blue"
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(createPMUserOpenModal());
                            }}
                        >
                            {messages['pmusers_main_add_user']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        columns={columns}
                        name="pm-users-main"
                        entity="pmusers"
                        infiniteScroll
                        clearOnUnmount={false}
                        graphqlConfig={graphqlConfig}
                        searchConfig={searchConfig}
                        exportConfig={exportConfig}
                        uiConfig={uiConfig}
                        selectionConfig={selectionConfig}
                        onChangeColumns={handleChangeColumns}
                        onChangeConditions={handleChangeConditions}
                        searchFields={searchFields}
                        tableButtons={['orderColumn']}
                        options={toolbarOptions}
                        onChangeTotal={handleChangeTotal}
                        menuOptions={menuOptions}
                        onClickMenu={handleClickMenu}
                        customColumn={customColumn}
                    />
                </Grid>
            </Paper>
            <CreateEditUser tableRef={tableRef} />
            <DeleteUser tableRef={tableRef} />
            <ForceLogout />
            {changeListOpenModalA && <ChangeList />}
            {generalDetailsOpenModal && <GeneralDetails />}
            {openModalResetEmail && <ResetEmail isPMUser={true} />}
            {openModalResendInvite && <ResendInvite isPMUser={true} />}
            {resetPasswordOModal && <ResetPassword isPMUser={true} />}
            {openModalActivities && <Activities noFilters2 noOpenSearch2 modalView noUsers height={innerHeight - 257} entity={"activitiesModalPMUsers"} instanceId="pm-users-tab-modal" />}
        </Container>
    )
}
export default PMUsers;